import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form, ListGroup, Spinner } from "react-bootstrap";
import "animate.css";
import "../css/addclienteform.css";

import { postTurno } from "../helpers/rutaTurnos";
import { getPersonas } from "../helpers/rutaPersonas";

/* ---------------- utils ---------------- */
const todayLocalISO = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const clean = (v) => String(v ?? "").trim();

const toIntOrZero = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const normalizePersonas = (r) =>
  Array.isArray(r?.personas)
    ? r.personas
    : Array.isArray(r)
    ? r
    : [];

/* ---------------- helpers Persona ---------------- */
const getPersonaId = (p) => clean(p?._id ?? p?.id ?? p?.value ?? "");

const getCodPersona = (p) =>
  Number(
    p?.codpersona ??
      p?.codper ??
      p?.codusu ??
      p?.codusuario ??
      p?.codigo ??
      p?.cod ??
      0
  ) || 0;

const getDescPersona = (p) => {
  const a = clean(p?.apellido);
  const n = clean(p?.nombre);
  const full = `${a} ${n}`.trim();

  return (
    full ||
    clean(p?.razonsocial) ||
    clean(p?.razonSocial) ||
    clean(p?.descripcion) ||
    clean(p?.nombrecompleto) ||
    clean(p?.nombreCompleto) ||
    ""
  );
};

/* ---------------- Atención ---------------- */
const ATENCION_OPTS = [
  { codigo: 0, descripcion: "SIN DEFINIR" },
  { codigo: 1, descripcion: "EN ESPERA" },
  { codigo: 2, descripcion: "ATENDIDO" },
  { codigo: 3, descripcion: "RECHAZADO" },
];

const getAtencionDesc = (cod) => {
  const n = Number(cod);
  const pick = ATENCION_OPTS.find((x) => x.codigo === n);
  return pick?.descripcion || "";
};

/* ---------------- initial ---------------- */
const initial = {
  codtur: "",
  fechaTurno: todayLocalISO(),
  codpersona: "",
  personaId: "",
  atencion: "0",
  nombre: "",
  telefono: "",
  observacion: "",
};

export default function AddTurnoForm({ setShow }) {
  const [formValues, setFormValues] = useState(initial);
  const [showSuccess, setShowSuccess] = useState(false);

  const [ui, setUi] = useState({
    personaDesc: "",
    atencionDesc: getAtencionDesc(0),
  });

  const [personasState, setPersonasState] = useState({
    data: [],
    loading: true,
  });

  useEffect(() => {
    getPersonas({ activo: true })
      .then((r) =>
        setPersonasState({
          data: normalizePersonas(r),
          loading: false,
        })
      )
      .catch(() =>
        setPersonasState({
          data: [],
          loading: false,
        })
      );
  }, []);

  const setField = useCallback((name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  useEffect(() => {
    setUi((prev) => ({
      ...prev,
      atencionDesc: getAtencionDesc(formValues.atencion),
    }));
  }, [formValues.atencion]);

  /* =========================================================
     PERSONA
  ========================================================= */
  const [showPerModal, setShowPerModal] = useState(false);
  const [qPer, setQPer] = useState("");
  const [perOpts, setPerOpts] = useState([]);
  const [loadingPer, setLoadingPer] = useState(false);
  const [selIdxPer, setSelIdxPer] = useState(0);
  const inputPerRef = useRef(null);

  const openPerModal = useCallback(() => {
    setShowPerModal(true);
    setQPer("");
    setPerOpts([]);
    setSelIdxPer(0);
    setTimeout(() => inputPerRef.current?.focus(), 50);
  }, []);

  const closePerModal = useCallback(() => {
    setShowPerModal(false);
  }, []);

  const assignPersona = useCallback((p) => {
    if (!p) return;

    const id = getPersonaId(p);
    const cod = String(getCodPersona(p) || "");
    const desc = getDescPersona(p);

    setFormValues((prev) => ({
      ...prev,
      codpersona: cod,
      personaId: id,
    }));

    setUi((prev) => ({
      ...prev,
      personaDesc: desc,
    }));
  }, []);

  const fetchPersonaByCod = useCallback(
    async (cod) => {
      const num = Number(cod || 0);

      if (!Number.isFinite(num) || num <= 0) {
        setUi((prev) => ({ ...prev, personaDesc: "" }));
        setFormValues((prev) => ({
          ...prev,
          codpersona: "",
          personaId: "",
        }));
        return;
      }

      const base = Array.isArray(personasState.data) ? personasState.data : [];

      const pick =
        base.find((x) => getCodPersona(x) === num) ||
        base.find((x) => String(getCodPersona(x)) === String(num)) ||
        null;

      if (pick) {
        assignPersona(pick);
      } else {
        setUi((prev) => ({ ...prev, personaDesc: "No encontrado" }));
        setFormValues((prev) => ({
          ...prev,
          personaId: "",
        }));
      }
    },
    [personasState.data, assignPersona]
  );

  const buscarPersona = useCallback(
    async (rawOverride) => {
      const raw = clean(rawOverride ?? formValues.codpersona);

      if (raw === "" || raw === "0") {
        openPerModal();
        return;
      }

      const cod = Number(raw);
      if (!Number.isFinite(cod) || cod <= 0) {
        openPerModal();
        return;
      }

      await fetchPersonaByCod(cod);
    },
    [formValues.codpersona, openPerModal, fetchPersonaByCod]
  );

  useEffect(() => {
    if (!showPerModal) return;

    const q = qPer.trim().toLowerCase();
    const base = Array.isArray(personasState.data) ? personasState.data : [];

    setLoadingPer(true);

    const t = setTimeout(() => {
      if (!q) {
        setPerOpts(base.slice(0, 50));
        setSelIdxPer(0);
        setLoadingPer(false);
        return;
      }

      const filtered = base
        .filter((p) => {
          const cod = String(getCodPersona(p));
          const desc = getDescPersona(p).toLowerCase();
          return cod.includes(q) || desc.includes(q);
        })
        .slice(0, 100);

      setPerOpts(filtered);
      setSelIdxPer(0);
      setLoadingPer(false);
    }, 180);

    return () => clearTimeout(t);
  }, [qPer, showPerModal, personasState.data]);

  const seleccionarPersona = useCallback(
    (p) => {
      if (!p) return;
      assignPersona(p);
      closePerModal();
    },
    [assignPersona, closePerModal]
  );

  const onPerModalKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closePerModal();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelIdxPer((i) => Math.min(i + 1, Math.max(0, perOpts.length - 1)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelIdxPer((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const pick = perOpts[selIdxPer] || perOpts[0];
      if (pick) seleccionarPersona(pick);
    }
  };

  /* =========================================================
     ATENCIÓN
  ========================================================= */
  const [showAteModal, setShowAteModal] = useState(false);
  const [qAte, setQAte] = useState("");
  const [ateOpts, setAteOpts] = useState(ATENCION_OPTS);
  const [selIdxAte, setSelIdxAte] = useState(0);
  const inputAteRef = useRef(null);

  const openAteModal = useCallback(() => {
    setShowAteModal(true);
    setQAte("");
    setAteOpts(ATENCION_OPTS);
    setSelIdxAte(0);
    setTimeout(() => inputAteRef.current?.focus(), 50);
  }, []);

  const closeAteModal = useCallback(() => {
    setShowAteModal(false);
  }, []);

  const assignAtencion = useCallback((opt) => {
    if (!opt) return;

    setFormValues((prev) => ({
      ...prev,
      atencion: String(opt.codigo),
    }));

    setUi((prev) => ({
      ...prev,
      atencionDesc: opt.descripcion,
    }));
  }, []);

  const fetchAtencionByCod = useCallback(
    (cod) => {
      const num = Number(cod);

      if (!Number.isFinite(num)) {
        setUi((prev) => ({ ...prev, atencionDesc: "" }));
        return;
      }

      const pick = ATENCION_OPTS.find((x) => x.codigo === num);
      if (pick) {
        assignAtencion(pick);
      } else {
        setUi((prev) => ({ ...prev, atencionDesc: "No encontrado" }));
      }
    },
    [assignAtencion]
  );

  const buscarAtencion = useCallback(
    (rawOverride) => {
        const raw = clean(rawOverride ?? formValues.atencion);

        if (raw === "") {
        openAteModal();
        return;
        }

        const cod = Number(raw);
        if (!Number.isFinite(cod)) {
        openAteModal();
        return;
        }

        fetchAtencionByCod(cod);
    },
    [formValues.atencion, openAteModal, fetchAtencionByCod]
  );

  useEffect(() => {
    if (!showAteModal) return;

    const q = qAte.trim().toLowerCase();

    if (!q) {
      setAteOpts(ATENCION_OPTS);
      setSelIdxAte(0);
      return;
    }

    const filtered = ATENCION_OPTS.filter((x) => {
      const cod = String(x.codigo);
      const desc = String(x.descripcion).toLowerCase();
      return cod.includes(q) || desc.includes(q);
    });

    setAteOpts(filtered);
    setSelIdxAte(0);
  }, [qAte, showAteModal]);

  const seleccionarAtencion = useCallback(
    (opt) => {
      if (!opt) return;
      assignAtencion(opt);
      closeAteModal();
    },
    [assignAtencion, closeAteModal]
  );

  const onAteModalKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeAteModal();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelIdxAte((i) => Math.min(i + 1, Math.max(0, ateOpts.length - 1)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelIdxAte((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const pick = ateOpts[selIdxAte] || ateOpts[0];
      if (pick) seleccionarAtencion(pick);
    }
  };

  /* ---------------- validate / payload ---------------- */
  const validate = () => {
    if (!clean(formValues.fechaTurno)) return "Ingresá la fecha del turno.";
    if (!clean(formValues.personaId)) return "Seleccioná una persona válida.";
    if (!clean(formValues.nombre)) return "Ingresá el nombre.";
    return "";
  };

  const buildPayload = () => {
    return {
      fechaTurno: clean(formValues.fechaTurno),
      persona: clean(formValues.personaId) || null,
      atencion: toIntOrZero(formValues.atencion),
      nombre: clean(formValues.nombre),
      telefono: clean(formValues.telefono),
      observacion: clean(formValues.observacion),
    };
  };

  const resetForm = () => {
    setFormValues(initial);
    setUi({
      personaDesc: "",
      atencionDesc: getAtencionDesc(0),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const msg = validate();
    if (msg) return alert(msg);

    const payload = buildPayload();

    try {
      await postTurno(payload);

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        resetForm();
        if (typeof setShow === "function") setShow(false);
      }, 900);
    } catch (err) {
      console.error(
        "❌ Error al crear turno:",
        err?.response?.status,
        err?.response?.data || err
      );
      alert("No se pudo guardar el turno. Revisá consola / Network.");
    }
  };

  const personasLoaded = useMemo(
    () => !personasState.loading,
    [personasState.loading]
  );

  return (
    <>
      {personasLoaded && (
        <div className="container">
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className="form-group mt-3 col-sm-2">
                <label>Código</label>
                <input
                  className="form-control"
                  name="codtur"
                  value={formValues.codtur}
                  onChange={handleChange}
                  placeholder="Auto"
                  disabled
                />
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label>Fecha Turno</label>
                <input
                  type="date"
                  className="form-control"
                  name="fechaTurno"
                  value={formValues.fechaTurno}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group mt-3 col-sm-2">
                <label>Persona (cod)</label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    name="codpersona"
                    value={formValues.codpersona}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        buscarPersona(e.currentTarget.value);
                      }
                    }}
                    onBlur={(e) => {
                    const v = clean(e.currentTarget.value);
                    if (v === "0") buscarAtencion(v);
                    else if (v) fetchAtencionByCod(v);
                    }}
                    min="0"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => buscarPersona()}
                    title="Buscar persona"
                    style={{
                      width: 44,
                      height: 38,
                      padding: 0,
                      fontSize: "1rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    🔎
                  </button>
                </div>
                <small className="text-muted">
                  <b>0</b> y Enter buscar
                </small>
              </div>

              <div className="form-group mt-3 col-sm-4">
                <label>Persona (desc.)</label>
                <input
                  className="form-control"
                  value={ui.personaDesc}
                  readOnly
                  placeholder="Se completa desde catálogo"
                />
              </div>

              <div className="form-group mt-3 col-sm-2">
                <label>Atención (cod)</label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    name="atencion"
                    value={formValues.atencion}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        buscarAtencion(e.currentTarget.value);
                      }
                    }}
                    onBlur={(e) => {
                      const v = clean(e.currentTarget.value);
                      if (v) fetchAtencionByCod(v);
                    }}
                    min="0"
                    max="3"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => buscarAtencion()}
                    title="Buscar atención"
                    style={{
                      width: 44,
                      height: 38,
                      padding: 0,
                      fontSize: "1rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    🔎
                  </button>
                </div>
              </div>

              <div className="form-group mt-3 col-sm-4">
                <label>Atención (desc.)</label>
                <input className="form-control" value={ui.atencionDesc} readOnly />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group mt-3 col-sm-6">
                <label>Nombre</label>
                <input
                  className="form-control"
                  name="nombre"
                  value={formValues.nombre}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label>Teléfono</label>
                <input
                  className="form-control"
                  name="telefono"
                  value={formValues.telefono}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group mt-3 col-sm-9">
                <label>Observación</label>
                    <textarea
                    className="form-control"
                    name="observacion"
                    value={formValues.observacion}
                    onChange={handleChange}
                    rows={6}
                    style={{
                        fontSize: "1.5rem",
                        lineHeight: "1.4",
                        minHeight: "100px",
                    }}
                    />
              </div>
            </div>

            <button type="submit" className="btn btn-dark mt-5 mb-3" id="button">
              Guardar
            </button>
          </form>
        </div>
      )}

      <Modal show={showPerModal} onHide={closePerModal} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Buscar persona</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Código / Nombre</Form.Label>
            <Form.Control
              ref={inputPerRef}
              value={qPer}
              placeholder="Ej: 2 o ines"
              onChange={(e) => setQPer(e.target.value)}
              onKeyDown={onPerModalKeyDown}
            />
            <Form.Text className="text-muted">
              ↑ ↓ para moverte • Enter selecciona • Esc cierra
            </Form.Text>
          </Form.Group>

          <div className="mt-3" style={{ maxHeight: 320, overflowY: "auto" }}>
            {loadingPer && (
              <div className="d-flex align-items-center gap-2 text-muted">
                <Spinner animation="border" size="sm" /> Buscando…
              </div>
            )}

            {!loadingPer && !perOpts.length && (
              <div className="text-muted">
                {qPer.trim()
                  ? "Sin resultados."
                  : "Escribí para buscar o elegí de la lista."}
              </div>
            )}

            {!!perOpts.length && (
              <ListGroup>
                {perOpts.map((p, idx) => {
                  const cod = getCodPersona(p);
                  const desc = getDescPersona(p);

                  return (
                    <ListGroup.Item
                      key={getPersonaId(p) || `${cod}-${desc}-${idx}`}
                      action
                      active={idx === selIdxPer}
                      onClick={() => seleccionarPersona(p)}
                    >
                      <b>{cod}</b> — {desc || "(sin nombre)"}
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closePerModal}>
            Cerrar
          </Button>
          <Button
            variant="primary"
            onClick={() => seleccionarPersona(perOpts[selIdxPer] || perOpts[0])}
            disabled={!perOpts.length}
          >
            Seleccionar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAteModal} onHide={closeAteModal} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Buscar atención</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Código / Descripción</Form.Label>
            <Form.Control
              ref={inputAteRef}
              value={qAte}
              placeholder="Ej: 1 o espera"
              onChange={(e) => setQAte(e.target.value)}
              onKeyDown={onAteModalKeyDown}
            />
            <Form.Text className="text-muted">
              ↑ ↓ para moverte • Enter selecciona • Esc cierra
            </Form.Text>
          </Form.Group>

          <div className="mt-3" style={{ maxHeight: 320, overflowY: "auto" }}>
            {!ateOpts.length && (
              <div className="text-muted">
                {qAte.trim()
                  ? "Sin resultados."
                  : "Escribí para buscar o elegí de la lista."}
              </div>
            )}

            {!!ateOpts.length && (
              <ListGroup>
                {ateOpts.map((opt, idx) => (
                  <ListGroup.Item
                    key={opt.codigo}
                    action
                    active={idx === selIdxAte}
                    onClick={() => seleccionarAtencion(opt)}
                  >
                    <b>{opt.codigo}</b> — {opt.descripcion}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeAteModal}>
            Cerrar
          </Button>
          <Button
            variant="primary"
            onClick={() => seleccionarAtencion(ateOpts[selIdxAte] || ateOpts[0])}
            disabled={!ateOpts.length}
          >
            Seleccionar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSuccess}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="animate__animated animate__fadeInDown"
      >
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>📅</div>
          <h5 className="mt-2">¡Turno generado correctamente!</h5>
        </Modal.Body>
      </Modal>
    </>
  );
}